import React, { useEffect, useState, useRef, useCallback } from 'react';
import { apiFetch, apiDownload } from '../../../../lib/api';
import { CommessaDettaglio } from '../SchedaCommessa';
import BACKEND_URL from '../../../../config.js';
import { API_DOCS } from './TabDocumenti.api';
import ApiInspector from '../../../../components/ApiInspector';

// ── Tipi ─────────────────────────────────────────────────────────────────────

interface Documento {
  id: number;
  nome: string;
  categoria?: string | null;
  nomeFile: string;
  uploadedAt: string;
  note?: string | null;
  uploadedById: number;
  uploadedByNome?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtDate = (d: string | null | undefined) =>
  d ? new Date(d).toLocaleDateString('it-IT') : '—';

const trunc = (s: string | null | undefined, n: number) =>
  s && s.length > n ? s.slice(0, n) + '…' : s ?? '';

const ext = (nome: string) => nome.split('.').pop()?.toLowerCase() ?? '';

function FileIcon({ nome }: { nome: string }) {
  const e = ext(nome);
  const isPdf = e === 'pdf';
  const isImg = ['jpg','jpeg','png','gif','webp','svg'].includes(e);
  const iconClass = isPdf ? 'bop-tab-doc__file-icon--pdf' : isImg ? 'bop-tab-doc__file-icon--img' : 'bop-tab-doc__file-icon--default';
  return (
    <div className={`bop-tab-doc__file-icon ${iconClass}`}>
      {isPdf ? '📄' : isImg ? '🖼️' : '📎'}
    </div>
  );
}

const CATEGORIE = ['Contratto','Progetto','Permessi','Corrispondenza','Fattura','Altro'];

// ── Componente ────────────────────────────────────────────────────────────────

export default function TabDocumenti({
  commessa, onRefresh,
}: {
  commessa: CommessaDettaglio;
  onRefresh: () => void;
}) {
  const [docs,    setDocs]    = useState<Documento[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  // Modal carica
  const [modal, setModal] = useState(false);
  const [form, setForm]   = useState({ nome: '', categoria: '', note: '' });
  const [file, setFile]   = useState<File | null>(null);
  const [drag, setDrag]   = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalErr, setModalErr] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  // ── Carica ─────────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      // @api GET /api/commesse/:id/documenti -> {"documenti":"Documento[]"}
      const res = await apiFetch<{ documenti: Documento[] }>(`/api/commesse/${commessa.id}/documenti`);
      setDocs(res.documenti ?? []);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [commessa.id]);

  useEffect(() => { load(); }, [load]);

  // ── Upload ─────────────────────────────────────────────────────────────────

  const apriUpload = () => {
    setForm({ nome: '', categoria: '', note: '' });
    setFile(null); setModalErr(''); setModal(true);
  };

  const onFileDrop = (f: File) => {
    setFile(f);
    if (!form.nome) setForm(prev => ({ ...prev, nome: f.name.replace(/\.[^.]+$/, '') }));
  };

  const carica = async () => {
    if (!file)           { setModalErr('Seleziona un file'); return; }
    if (!form.nome.trim()) { setModalErr('Il nome è obbligatorio'); return; }
    if (file.size > 20 * 1024 * 1024) { setModalErr('Il file supera i 20 MB'); return; }

    setSaving(true); setModalErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('nome', form.nome.trim());
      if (form.categoria) fd.append('categoria', form.categoria);
      if (form.note.trim()) fd.append('note', form.note.trim());

      const token = localStorage.getItem('token');
      // @api POST /api/commesse/:id/documenti {"file":"File","nome":"string","categoria":"string","note":"string"} -> {"ok":"true","documento":"Documento"}
      const res = await fetch(`${BACKEND_URL}/api/commesse/${commessa.id}/documenti`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as any).error || `Errore ${res.status}`);
      }
      setModal(false); load();
    } catch (e: any) { setModalErr(e.message); }
    finally { setSaving(false); }
  };

  // ── Elimina ────────────────────────────────────────────────────────────────

  const elimina = async (doc: Documento) => {
    if (!window.confirm(`Eliminare il documento "${doc.nome}"? L'operazione è irreversibile.`)) return;
    try {
      // @api DELETE /api/commesse/:id/documenti/:docId -> {"ok":"true"}
      await apiFetch(`/api/commesse/${commessa.id}/documenti/${doc.id}`, { method: 'DELETE' });
      load();
    } catch (e: any) { setError(e.message); }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Header */}
      <div className="bop-tab-doc__header">
        <button className="bop-btn bop-btn-primary" onClick={apriUpload}>Carica Documento</button>
      </div>

      {error && <div className="inline-error">{error}</div>}

      {loading ? (
        <div className="inline-loading">Caricamento documenti…</div>
      ) : docs.length === 0 ? (
        <div className="bop-empty-state">Nessun documento caricato.</div>
      ) : (
        <div className="bop-tab-doc__grid">
          {docs.map(doc => (
            <div key={doc.id} className="bop-tab-doc__card">
              <div className="bop-tab-doc__card-top">
                <FileIcon nome={doc.nomeFile} />
                <div className="bop-tab-doc__card-info">
                  <div className="bop-tab-doc__card-name">
                    {doc.nome}
                  </div>
                  {doc.categoria && (
                    <span className="bop-badge bop-badge--info bop-tab-doc__badge-cat">{doc.categoria}</span>
                  )}
                </div>
              </div>

              {doc.note && (
                <div className="bop-tab-doc__card-note" title={doc.note}>
                  {trunc(doc.note, 80)}
                </div>
              )}

              <div className="bop-tab-doc__card-meta">
                Caricato da <strong>{doc.uploadedByNome ?? '—'}</strong> il {fmtDate(doc.uploadedAt)}
              </div>

              <div className="bop-tab-doc__card-actions">
                {/* @api GET /api/commesse/:id/documenti/:docId/download -> file stream (attachment) */}
                <button
                  className="bop-btn bop-btn-secondary bop-tab-doc__btn-download"
                  onClick={() => apiDownload(`/api/commesse/${commessa.id}/documenti/${doc.id}/download`, doc.nomeFile || doc.nome || 'documento')}
                >
                  Download
                </button>
                <button className="bop-btn bop-btn-danger bop-btn-ghost bop-btn-sm" onClick={() => elimina(doc)}>
                  Elimina
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Modal Carica ── */}
      {modal && (
        <div className="bop-modal-overlay">
          <div className="bop-modal bop-modal--sm">
            <div className="bop-modal__header">
              <h3 className="bop-modal__title">Carica Documento</h3>
              <button className="bop-modal__close"
                onClick={() => setModal(false)}>×</button>
            </div>
            <div className="bop-modal__body">
              {modalErr && <div className="inline-error">{modalErr}</div>}

              <div className="bop-form-group">
                <label className="bop-label">Nome documento <span className="bop-text-danger">*</span></label>
                <input className="bop-input" value={form.nome}
                  placeholder="es. Contratto principale"
                  onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} />
              </div>

              <div className="bop-form-group">
                <label className="bop-label">Categoria</label>
                <select className="bop-input" value={form.categoria}
                  onChange={e => setForm(f => ({ ...f, categoria: e.target.value }))}>
                  <option value="">Seleziona…</option>
                  {CATEGORIE.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              {/* Drag & drop area */}
              <div className="bop-form-group">
                <label className="bop-label">File <span className="bop-text-danger">*</span></label>
                <div
                  onDragOver={e => { e.preventDefault(); setDrag(true); }}
                  onDragLeave={() => setDrag(false)}
                  onDrop={e => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files[0]; if (f) onFileDrop(f); }}
                  onClick={() => fileRef.current?.click()}
                  className={`bop-tab-doc__dropzone ${drag ? 'bop-tab-doc__dropzone--active' : ''}`}>
                  {file ? (
                    <div className="bop-tab-doc__dropzone-file">
                      <div className="bop-tab-doc__dropzone-file-icon">{ext(file.name) === 'pdf' ? '📄' : '📎'}</div>
                      <div className="bop-tab-doc__dropzone-file-name">{file.name}</div>
                      <div className="bop-tab-doc__dropzone-file-size">{(file.size / 1024 / 1024).toFixed(2)} MB</div>
                    </div>
                  ) : (
                    <div className="bop-tab-doc__dropzone-empty">
                      <div className="bop-tab-doc__dropzone-empty-icon">☁️</div>
                      <div className="bop-tab-doc__dropzone-empty-text">
                        Trascina un file qui oppure <span className="bop-tab-doc__dropzone-link">scegli dal computer</span>
                      </div>
                      <div className="bop-tab-doc__dropzone-hint">Qualsiasi tipo di file, max 20 MB</div>
                    </div>
                  )}
                </div>
                <input ref={fileRef} type="file" className="bop-tab-doc__file-input-hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) onFileDrop(f); }} />
              </div>

              <div className="bop-form-group">
                <label className="bop-label">Note</label>
                <textarea className="bop-input" rows={2} value={form.note}
                  onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
              </div>
            </div>
            <div className="bop-modal__footer">
              <button className="bop-btn bop-btn-secondary" onClick={() => setModal(false)}>Annulla</button>
              <button className="bop-btn bop-btn-primary" onClick={carica} disabled={saving}>
                {saving ? 'Caricamento…' : 'Carica'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ApiInspector docs={API_DOCS} />
    </div>
  );
}
